import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Battery, ChevronRight, Zap, Shield, Sparkles, Leaf, Phone, ClipboardList, CheckCircle, Wrench } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { getCQ7Package, CQ7_INVERTERS, CQ7_PACKAGES, startingPrice, fmtAUD } from '../data/cq7Packages';

const CQ7PackagePage = () => {
  const { packageSlug } = useParams();
  const pkg = getCQ7Package(packageSlug);
  if (!pkg) return <Navigate to="/packages" replace />;

  const minPrice = startingPrice(pkg);
  const cheapestInverter = CQ7_INVERTERS.find((i) => pkg.prices[i.id] === minPrice);
  const otherPackages = CQ7_PACKAGES.filter((p) => p.slug !== pkg.slug);

  return (
    <div className="min-h-screen">
      <SEO
        title={`Fox ESS CQ7 ${pkg.kwh} kWh Battery Package — From ${fmtAUD(minPrice)} Installed*`}
        description={`Fox ESS CQ7 ${pkg.kwh} kWh modular battery package (${pkg.config}) fully supplied and installed by Supa Energy Hub. From ${fmtAUD(minPrice)} after BSTC rebate. Choose from 5 Fox ESS hybrid inverter options.`}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-5" data-testid="cq7-breadcrumb">
              <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/packages" className="hover:text-[#3D5A47]">Packages</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#3D5A47] font-medium">Fox ESS CQ7 {pkg.kwh} kWh</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
                  <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">Fox ESS Package · Fully Installed</span>
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                  Fox ESS CQ7 <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">{pkg.kwh} kWh</span> Package
                </h1>
                <p className="text-sm text-gray-500 mt-2 font-medium uppercase tracking-wide">{pkg.config}</p>
                <p className="text-base sm:text-lg text-gray-700 mt-4 max-w-2xl leading-relaxed">
                  High-voltage modular LFP battery from Fox ESS — fully supplied and installed by Supa Energy Hub.
                  Pair with one of five Fox ESS hybrid inverter options to suit your phase configuration and solar size.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to="/get-estimate?source=cq7-package">
                    <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="cq7-hero-cta">
                      Request My Quote <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0478453548">
                    <Button variant="outline" className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl">
                      <Phone className="w-4 h-4" /> Call 0478 453 548
                    </Button>
                  </a>
                </div>
                <p className="text-[11px] text-gray-500 mt-4">
                  *Price after applicable Battery STC (BSTC) rebate is assigned to Supa Energy Hub. Standard install conditions apply.
                </p>
              </div>

              {/* Starting From card */}
              <div className="lg:col-span-5">
                <div className="bg-white rounded-2xl border border-[#B5D43B]/30 p-6 sm:p-7 shadow-xl relative">
                  <div className="absolute -top-3 left-6 bg-[#B5D43B] text-[#1a2820] text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                    Starting from
                  </div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1 mt-3">
                    Installed after BSTC rebate
                  </p>
                  <p className="text-5xl sm:text-6xl font-black text-[#3D5A47] leading-none mt-2" data-testid="cq7-starting-price">
                    {fmtAUD(minPrice)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    With <strong>{cheapestInverter?.short}</strong> hybrid inverter. Other inverter options shown below.
                  </p>
                  <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="uppercase tracking-widest text-gray-400 font-semibold mb-1">Capacity</p>
                      <p className="font-bold text-[#3D5A47]">{pkg.kwh} kWh</p>
                    </div>
                    <div>
                      <p className="uppercase tracking-widest text-gray-400 font-semibold mb-1">BSTC Discount</p>
                      <p className="font-bold text-[#3D5A47]">{fmtAUD(pkg.bstcDiscount)} ({pkg.bstcQty} STCs)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INVERTER OPTIONS TABLE */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Choose your inverter</span>
            <h2 className="heading-2 text-[#3D5A47] mb-2">5 hybrid inverter options</h2>
            <p className="text-gray-600 text-sm mb-6 max-w-3xl">
              The {pkg.kwh} kWh CQ7 battery pairs with any of these five Fox ESS hybrid inverters.
              Choose based on your home&rsquo;s phase configuration (single or three-phase) and your existing or planned solar size.
            </p>
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="cq7-inverter-table">
              <table className="w-full text-sm">
                <thead className="bg-[#3D5A47] text-white text-left">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Inverter</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Phase</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold text-right">Installed Price (after rebate*)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {CQ7_INVERTERS.map((inv) => {
                    const isMin = pkg.prices[inv.id] === minPrice;
                    return (
                      <tr key={inv.id} className={isMin ? 'bg-[#B5D43B]/10' : ''} data-testid={`cq7-row-${inv.id}`}>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-[#3D5A47]" />
                            <span className="font-semibold text-[#3D5A47]">{inv.short}</span>
                            {isMin && (
                              <span className="text-[10px] uppercase tracking-widest bg-[#B5D43B] text-[#1a2820] font-bold px-2 py-0.5 rounded-full">
                                Best Value
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-gray-600">{inv.id.includes('3p') ? 'Three-Phase' : 'Single-Phase'}</td>
                        <td className="px-4 sm:px-6 py-4 text-right font-bold text-[#3D5A47]">{fmtAUD(pkg.prices[inv.id])}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              *Prices assume standard installation and customer assignment of the {pkg.bstcQty} BSTC rebates (~{fmtAUD(pkg.bstcDiscount)} discount) to Supa Energy Hub. Additional site-specific costs may apply.
            </p>
          </div>
        </section>

        {/* WHY CQ7 — quick spec strip */}
        <section className="py-16 bg-[#f8faf5]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Why CQ7</span>
            <h2 className="heading-2 text-[#3D5A47] mb-6">Flagship Fox ESS battery technology</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cq7-features">
              {[
                { icon: Battery, t: '100% Usable DOD', d: 'Every stored kWh is yours to use — no derated capacity.' },
                { icon: Leaf, t: 'LFP Chemistry', d: 'Safest mainstream lithium — long cycle life, thermally stable.' },
                { icon: Zap, t: '80 A High Current', d: 'Higher continuous current than CQ6 for larger inverter loads.' },
                { icon: Shield, t: 'IP65 Outdoor-Rated', d: 'Designed to live outside — dust-tight and weather sealed.' },
              ].map((f, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-3">
                    <f.icon className="w-5 h-5 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{f.t}</h3>
                  <p className="text-xs text-gray-600">{f.d}</p>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Link to="/products/batteries/fox-ess-cq7" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                See full CQ7 technical specs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="py-16 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-[#3D5A47] mb-2">What&rsquo;s Included in the Standard Installation Price?</h2>
            <p className="text-gray-600 mb-6 text-sm">
              The advertised CQ7 package price is based on a <strong>standard installation</strong>. Here&rsquo;s everything that&rsquo;s covered:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="cq7-included-list">
              {[
                'Fox ESS CQ7 battery modules (per configuration)',
                'Fox ESS hybrid inverter (chosen option)',
                'Smart meter supply and installation',
                'Standard delivery',
                'Standard installation',
                'Easy access to battery installation location',
                'Easy access to switchboard',
                'Battery installed within 5 m of the switchboard',
                'Clear and unobstructed cabling pathway',
                'Switchboard has enough space for smart meter and required switches',
                'Existing flat, level, sturdy concrete base / slab available',
                'Suitable fire-resistant wall behind battery location',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 bg-[#f8faf5] rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ADDITIONAL COSTS */}
        <section className="py-16 bg-[#fdf4ec] border-y border-orange-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="heading-2 text-[#3D5A47] mb-2">Additional Costs That May Apply</h2>
                <p className="text-gray-700 text-sm max-w-3xl">
                  The advertised CQ7 package price is based on standard installation conditions. Some homes may require extra work depending on site condition, switchboard, battery location, cable run, wall type, and safety requirements.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden border border-orange-100 shadow-sm" data-testid="cq7-extras-table">
              <table className="w-full text-sm">
                <thead className="bg-orange-50 text-[#3D5A47] text-left">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Additional Item</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Additional Cost / Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['Bollards, if required', '$200 per site'],
                    ['Awning, if required', '$200 per site'],
                    ['Additional cabling beyond 5 m', '$25 per metre after the first 5 m'],
                    ['Concrete slab / base, if required', '$150'],
                    ['Fireproof backing / cement sheet, if required', '$150'],
                    ['Additional switchboard works', 'To be estimated before installation'],
                    ['Obstructed cabling pathway', 'Based on additional labour and materials'],
                    ['Three-phase wiring upgrades (if required)', 'To be estimated before installation'],
                    ['Any other site-specific works', 'Based on additional labour and materials'],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td className="px-4 sm:px-6 py-3 text-gray-700">{k}</td>
                      <td className="px-4 sm:px-6 py-3 font-medium text-[#3D5A47]">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-600 mt-4 italic">
              Some additional costs can only be accurately confirmed after a technician inspects the site, including the switchboard, battery location, cable pathway and installation conditions. A site inspection deposit may apply — our team will confirm at the time of quoting.
            </p>
          </div>
        </section>

        {/* OTHER CQ7 PACKAGES */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Need more (or less) capacity?</span>
                <h2 className="heading-2 text-[#3D5A47]">Other CQ7 packages</h2>
              </div>
              <Link to="/packages" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                All packages <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cq7-other-packages">
              {otherPackages.map((p) => (
                <Link
                  key={p.slug}
                  to={`/packages/${p.slug}`}
                  className="group bg-white rounded-xl border border-gray-100 hover:border-[#B5D43B] hover:shadow-md transition-all p-4 text-center"
                  data-testid={`cq7-other-${p.slug}`}
                >
                  <p className="text-2xl font-black text-[#3D5A47] group-hover:text-[#B5D43B] leading-none">{p.kwh}</p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">kWh</p>
                  <p className="text-xs text-gray-600 mt-2">From {fmtAUD(startingPrice(p))}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-black mb-3">Lock in your {pkg.kwh} kWh Fox ESS package</h2>
            <p className="text-white/80 mb-6">Free quote. We handle the BSTC rebate paperwork and install everything end-to-end.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/get-estimate?source=cq7-package">
                <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] gap-2 font-bold px-6 py-5 rounded-xl">
                  <ClipboardList className="w-4 h-4" /> Get My Quote
                </Button>
              </Link>
              <a href="tel:0478453548">
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 px-6 py-5 rounded-xl">
                  <Phone className="w-4 h-4" /> Call 0478 453 548
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CQ7PackagePage;
